import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { ScreenSizeDetectorService, SnackBarComponent, StepperDialogComponent, UiKitModule } from '@serviceplan/ui-kit';
import { DownloadAssetsService } from '@thehubz/client/asset-services';
import {
  AppSettings,
  AssetUIModel,
  AssetsGenerateUrlService,
  CanPipe,
  ClientCoreModule,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  ENVIRONMENT,
  FileObject,
  HasRoleDirective,
  ValueListsService,
  ViewType,
  accessAsset,
  createAssetFileKey,
} from '@thehubz/client/core';

import { Asset, AssetDetails, AssetsApiService, AssetsBulkEditApiService, UpdateAssetDto } from '@thehubz/client/data-access-api';
import { FileUploadService } from '@thehubz/client/file-upload';
import { CustomClients } from '@thehubz/shared/branding';
import { ANONYMOUS_USER_EMAIL, AnyType } from '@thehubz/shared/model';
import { Subscription, lastValueFrom, tap } from 'rxjs';
import { AssetCommonService } from '../../services/asset-common.service';
import { AssetUploadService } from '../../services/asset-upload.service';
import { AssetViewTypeService } from '../../services/asset-view-type.service';
import { CollectionStepperDialogService } from '../../services/collection-stepper-dialog.service';
import { SharingStepperDialogService } from '../../services/sharing-stepper-dialog.service';
import { BuyoutUtilsService } from '../asset-buyout-rights/buyout-utils.service';
import { AssetDownloadComponent, AssetDownloadData } from '../asset-download/asset-download.component';
import { AssetEditPopupComponent } from '../asset-edit-popup/asset-edit-popup.component';
import { isUsageRightsEdit } from '../assets/assets-page.util';
import { AssetsPageService } from '../assets/assets-page/assets-page.service';
import { UploadVariantsComponent } from '../upload-variants/upload-variants.component';
import { AssetDeletionDialogComponent } from './asset-deletion-dialog/asset-deletion-dialog.component';
import { AssetExportDownloadsComponent } from './asset-export-downloads/asset-export-downloads.component';
import { AssetRelationsService } from './asset-relations/asset-relations.service';
import { AssetsDetailViewRoutingPaths } from './assets-detail-view-routing.module';
import { newAssetVersionStepperConfig } from './new-asset-version.stepper';

@Component({
  selector: 'thehubz-asset-detail-view',
  templateUrl: './asset-detail-view.component.html',
  styleUrls: ['./asset-detail-view.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterOutlet, MatIconModule, MatMenuModule, ClientCoreModule, UiKitModule, HasRoleDirective],
  providers: [AssetRelationsService],
})
export class AssetDetailViewComponent implements OnInit, OnDestroy {
  navLinks: {
    path: string;
    label: string;
    alternateLabel?: string;
  }[] = [];
  files: FileObject[] = [];
  assetInfo!: AssetUIModel;
  deepCopiedAssetInfo!: AssetUIModel;
  users!: { id: string; name: string; email: string }[];
  userSelected!: { name: string; _id: string };
  currentIndex = 0;
  progress = { percent: 0 };
  assetFilteredDetails: AnyType;
  storedAssetsIds: string[] = [];
  tags!: string;
  uploadInProgress = false;
  currentTab!: AssetsDetailViewRoutingPaths;
  assetAspectRatio: AssetDetails[] = [];
  brandingKey!: CustomClients;
  snackBarRef!: MatSnackBarRef<SnackBarComponent>;

  uploadStartSubscription?: Subscription;
  uploadCompleteSubscription?: Subscription;
  progressSubscription?: Subscription;
  assetIDSubjectSubscription?: Subscription;

  dialogEditRef!: MatDialogRef<AssetEditPopupComponent>;
  dialogRef!: MatDialogRef<UploadVariantsComponent>;
  assetExpiredDays = 0;

  AccessAsset = accessAsset;
  AssetTypeEnum = Asset.AssetTypeEnum;
  CustomClients = CustomClients;
  gridSettingsKeySuffix = 'RelationsTab';

  editTypeTexts = {
    metadata: $localize`:@@editMetadata:Edit metadata`,
    usageRights: $localize`:@@editUsageRights:Edit usage rights`,
    relations: $localize`:@@editRelations:Edit relations`,
  };

  menuItemButtonContext = {
    buttonType: 'mat-menu-item',
    buttonClass: 'material-icons-outlined',
  };

  flatButtonContext = {
    buttonType: 'mat-flat-button',
    buttonClass: 'w-auto button-primary mr-4',
  };

  assetsDetailViewTabs = AssetsDetailViewRoutingPaths;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private router: Router,
    private canPipe: CanPipe,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private assetService: AssetsApiService,
    private commonUtilityLib: CommonUtilityLib,
    private fileUploadService: FileUploadService,
    private valueListsService: ValueListsService,
    private assetsPageService: AssetsPageService,
    private assetUploadService: AssetUploadService,
    private buyoutUtilsService: BuyoutUtilsService,
    private assetCommonService: AssetCommonService,
    private assetUrlService: AssetsGenerateUrlService,
    private downloadAssetsService: DownloadAssetsService,
    private bulkEditApiService: AssetsBulkEditApiService,
    protected assetViewTypeService: AssetViewTypeService,
    protected assetRelationsService: AssetRelationsService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
    private sharingStepperDialogService: SharingStepperDialogService,
    private collectionStepperDialogService: CollectionStepperDialogService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit(): void {
    this.currentTab = this.currentTabValue();
    this.storedAssetsIds = JSON.parse(sessionStorage.getItem('assetsGalleryIdsList') || '[]');

    this.assetFilteredDetails = JSON.parse(sessionStorage.getItem(AppSettings.ASSET_FILTER_STRING) || '{}');
    this.assetIDSubjectSubscription = this.assetCommonService.assetIDSubject.subscribe((isNext: boolean) => {
      if (isNext !== null) {
        this.changeAsset(isNext);
      }
    });
    this.getAssetDetail();
    this.fileUploadsSubscription();
    this.assetCommonService.assetDataSubject$.subscribe((asset) => {
      if (asset) {
        this.assetInfo = asset;
        this.deepCopiedAssetInfo = JSON.parse(JSON.stringify(asset));
        if (asset.expiryDate) {
          this.assetExpiredDays = this.assetCommonService.getAssetExpireDays(asset.expiryDate);
        }

        // navigation links object
        this.navLinks = this.activatedRoute?.routeConfig?.children
          ? this.commonUtilityLib.buildNavItems(this.activatedRoute.routeConfig.children, undefined, this.assetInfo, false, this.brandingKey)
          : [];
      }
    });
  }

  currentTabClick(path: string) {
    this.currentTab = path as AssetsDetailViewRoutingPaths;
  }

  editAssets(editType: string) {
    const selectedAssets = this.deepCopiedAssetInfo;
    const assetObject = {
      editType: editType,
      assets: [selectedAssets],
    };
    this.assetsPageService.editDialogConfirmation(assetObject.assets).subscribe((data: AnyType) => {
      if (data) {
        if (isUsageRightsEdit(editType) && this.assetsPageService.isSupportedBranding()) {
          // Handle the usage rights edit for the provided assets
          this.assetsPageService.handleUsageRightsEdit(assetObject.assets);
        } else {
          // Open the edit dialog with the provided event (metadata & relations)
          this.openEditDialog(assetObject);
        }
      }
    });
  }

  /**
   * Opens an edit dialog for the other edits (metadata & relations) of assets
   * @param event
   */
  private async openEditDialog(assetObject: { editType: string; assets: AssetUIModel[] }) {
    let getProductionDetailsEditModel: AnyType;

    if (assetObject.editType === 'metaData' && this.brandingKey === CustomClients.Standard) {
      getProductionDetailsEditModel = await lastValueFrom(
        this.bulkEditApiService.assetsBulkEditControllerGetProductionDetailsEditModel({ ids: assetObject.assets.map((asset) => asset._id) }),
      );
      getProductionDetailsEditModel = !getProductionDetailsEditModel.assetsWithoutProductionDetails ? getProductionDetailsEditModel : undefined;
    }

    this.dialogEditRef = this.dialog.open(AssetEditPopupComponent, {
      maxWidth: '120rem',
      width: '90%',
      height: '90%',
      data: { ...assetObject, productionDetails: getProductionDetailsEditModel },
    });
    this.dialogEditRef.disableClose = true;
    this.dialogEditRef.afterClosed().subscribe(async (result: AssetUIModel[]) => {
      if (result) {
        const currentUrl = this.router.url.split('?')[0];
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate([currentUrl]);
        });
      }
    });
  }

  newBuyoutRights() {
    this.assetsPageService.editDialogConfirmation([this.assetInfo]).subscribe((data: AnyType) => {
      if (data) {
        this.buyoutUtilsService
          .assetBuyoutRightsDialogs([this.assetInfo], false, true)
          .subscribe((update) => this.assetCommonService.assetDataSubject$.next(update.updatedAsset));
      }
    });
  }

  async newAssetVersion() {
    let tags: string[] = [];
    if (this.assetInfo?.tags?.length) {
      const tagIds: string[] = this.assetInfo?.tags as string[];
      const tagsInfo = await this.valueListsService.listTags(tagIds);
      tags = tagsInfo.map((tag) => tag.name);
    }
    const tagsAccessibility = !this.canPipe.transform(this.AccessAsset(this.assetInfo), 'tagging');
    const stepperData = newAssetVersionStepperConfig(this.assetInfo, this.users, tags, { disableTag: tagsAccessibility });

    this.dialog
      .open(StepperDialogComponent, {
        width: '90%',
        height: '90%',
        maxWidth: '100vw',
        disableClose: true,
        panelClass: 'custom-dialog-container',
        data: { stepperData },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.uploadInProgress = true;
          this.files = result.data.uploadAssets.assetData;
          this.assetInfo.tags = this.tags?.split(',');
          this.startUpload(result.data.uploadAssets.assetData);
        }
      });
  }

  private getUsers(): void {
    const agencyID = this.assetInfo.agency?._id.toString() ?? this.assetInfo.domains?.[0].agency;
    const clientID = this.assetInfo.client?._id.toString() ?? this.assetInfo.domains?.[0].client;
    const brandID = this.assetInfo.brand?._id.toString() ?? this.assetInfo.domains?.[0].brand;
    if (agencyID) {
      this.valueListsService.listUserByACB(agencyID, clientID, brandID, ['global.library']).subscribe((users) => {
        this.assetCommonService.userACBSubject$.next(users);
        this.users = users.map((usr) => {
          if (usr.id === this.assetInfo.owner) {
            this.userSelected = { name: usr.name, _id: usr.id };
          }
          return { id: usr.id, name: usr.name, email: usr.email };
        });
      });
    }
  }

  private getAssetDetail(assetID?: string) {
    if (!assetID) {
      const currentPath = this.router.url.split('/');
      assetID = currentPath[currentPath.length - 2];
      this.currentIndex = this.storedAssetsIds.findIndex((storedAssetId: string) => storedAssetId == assetID);
    }
    const loggedInUser = this.assetCommonService.currentUser.userData.email;
    this.assetService.assetsControllerFindOne(assetID).subscribe({
      next: async (asset) => {
        await this.setAssetDetails(asset);
        if (loggedInUser !== ANONYMOUS_USER_EMAIL) this.getUsers();
      },
      error: async (err) => {
        if (err.status === 405) {
          if (loggedInUser === ANONYMOUS_USER_EMAIL) {
            await this.router.navigate(['/login'], { queryParams: { redirectUrl: this.router.url } });
          } else {
            await this.router.navigate(['/assets']);
            this.clientNotificationHandlerService.errorNotification(err);
          }
        }
      },
    });
  }

  async setAssetDetails(assetObj: AssetUIModel) {
    const currentVersion = assetObj.currentVersion?.version;

    if (assetObj.currentVersion?.aspectRatios) {
      this.assetAspectRatio = [
        {
          assetId: assetObj._id,
          name: assetObj.name,
          version: assetObj.currentVersion?.version || 0,
          aspectRatios: assetObj.currentVersion?.aspectRatios || [],
        },
      ];
    }

    this.assetInfo = this.assetUrlService.addPreviewUrl(assetObj, currentVersion);
    this.commonUtilityLib.emitNewHeaderline(this.assetInfo.name);
    this.assetCommonService.assetDataSubject$.next(this.assetInfo);
    this.tags = this.assetInfo.tags?.toString() || '';
    if (this.assetInfo?.expiryDate) {
      this.assetExpiredDays = this.assetCommonService.getAssetExpireDays(this.assetInfo.expiryDate);
    }
  }

  changeAsset(isNext: boolean) {
    this.currentIndex = isNext ? this.currentIndex + 1 : this.currentIndex - 1;

    if (this.currentIndex < 0) {
      this.currentIndex = this.storedAssetsIds.length - 1;
    } else if (this.currentIndex > this.storedAssetsIds.length - 1) {
      this.currentIndex = 0;
    }

    if (isNext && (this.currentIndex + 1) % 48 === 0) {
      this.assetFilteredDetails.skip += 48;
      this.assetService.assetsControllerFindAll('', JSON.stringify(this.assetFilteredDetails)).subscribe({
        next: async (response) => {
          this.storedAssetsIds = this.storedAssetsIds.concat(response.result.map((asset) => asset._id));
          sessionStorage.setItem('assetsGalleryIdsList', JSON.stringify(this.storedAssetsIds));
          const responseData = this.assetUrlService.generatePreview(response.result ?? []);
          this.assetCommonService.updateAssetList([...this.assetCommonService.storedAssetsList, ...responseData]);
          this.setAssetInformation();
        },
        error: (err) => {
          this.clientNotificationHandlerService.errorNotification(err);
        },
      });
    } else {
      this.setAssetInformation();
    }
  }

  /**
   * This method returns the current tab value as a string.
   * It accesses the router's URL and performs a series of string manipulations
   * to extract the desired information.
   * @returns
   */
  currentTabValue(): AssetsDetailViewRoutingPaths {
    // Get the current URL from the router
    const url = this.router.url;
    // Find the last index of '/' in the URL
    const lastIndex = url.lastIndexOf('/');
    // Extract the text following the last '/' and any query parameters
    const textWithQuery = url.substring(lastIndex + 1);
    // Remove any query parameters from the text
    const text = textWithQuery.split('?')[0];
    // Return the final text, which is the current tab value
    return text as AssetsDetailViewRoutingPaths;
  }

  setAssetInformation() {
    const currentAssetId = this.storedAssetsIds[this.currentIndex];
    this.currentTab = this.currentTabValue();
    if (this.currentIndex > -1 && currentAssetId) {
      const currentUrl = 'assets/detail/' + currentAssetId + '/' + this.currentTab;
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([currentUrl]);
      });
      this.getAssetDetail(currentAssetId);
    }
  }

  addToCollection() {
    this.collectionStepperDialogService.openDialogForCollection([this.assetInfo]);
  }

  async sharedAssetsStepper() {
    const stepperData = await this.sharingStepperDialogService.createStepperModel(
      this.assetAspectRatio,
      $localize`Share assets: <span class="font-light">${this.assetInfo.name}<span>`,
    );
    this.sharingStepperDialogService.openSharingDialog(stepperData);
  }

  addNewVariant() {
    const aspectRatioCount = this.getNextAspectRatioId();
    const assetPickedProperties = (({ _id: assetId, assetType }) => ({ assetId, assetType }))(this.assetInfo);
    this.dialogRef = this.dialog.open(UploadVariantsComponent, {
      data: { ...assetPickedProperties, aspectRatioCount: aspectRatioCount },
      minWidth: '80%',
    });
    this.dialogRef.disableClose = true;
    this.dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        const currentAssetId = this.assetInfo._id;
        this.currentTab = this.currentTabValue();
        const currentUrl = 'assets/detail/' + currentAssetId + '/' + this.currentTab;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate([currentUrl]);
        });
      }
    });
  }

  private getNextAspectRatioId() {
    let num = 1;
    if (this.assetInfo.currentVersion?.aspectRatios) {
      const last = [...this.assetInfo.currentVersion.aspectRatios]
        .sort((a, b) => parseInt(a.aspectRatio.replace(/\D/g, '')) - parseInt(b.aspectRatio.replace(/\D/g, '')))
        .pop();
      num = parseInt(last?.aspectRatio.replace(/\D/g, '') || '1') + 1;
    }
    return num;
  }

  exportDownloads(): void {
    const exportDialog = this.dialog.open(AssetExportDownloadsComponent, { width: '100%', maxWidth: '50rem', data: this.assetInfo.name });
    exportDialog.afterClosed().subscribe((res) => {
      if (res) this.assetCommonService.assetDownloadExcel.next(true);
    });
  }

  /*-------------------- File upload and assets funcs ---------------------*/

  async startUpload(files: FileObject[]) {
    try {
      for await (const fileObj of files) {
        if (!fileObj.uploaded) {
          const incompleteUpload = await this.assetUploadService.checkForIncompleteUploads(fileObj, files);
          if (typeof incompleteUpload === 'boolean') {
            const asset = this.assetInfo;
            const key = createAssetFileKey(asset, undefined, true);
            const fileIndex = files.findIndex((fileOb) => fileOb.name === fileObj.name);
            const file = files[fileIndex];
            files[fileIndex] = { ...file, key: key };
            this.snackBarRef = this.snackBar.openFromComponent(SnackBarComponent, {
              data: { message: files[0].name, duration: this.progress },
              verticalPosition: 'top',
              horizontalPosition: 'end',
            });
            await this.assetUploadService.uploadVersionFile(this.assetInfo._id, {
              key: key,
              file: fileObj.file,
              name: fileObj.name,
            });
            this.getAssetDetail(this.assetInfo._id);
          }
        }
      }
    } catch (err) {
      this.uploadInProgress = false;
      this.clientNotificationHandlerService.errorNotification(err);
    }
  }

  fileUploadsSubscription(): void {
    this.progressSubscription = this.fileUploadService.progress.subscribe((data) => {
      if (data !== 0) {
        this.progress.percent = data;
      }
    });
    this.uploadStartSubscription = this.fileUploadService.fileUploadStartSubject.subscribe({
      next: (fileData) => {
        this.assetUploadService.updateFileDataOnStart(fileData, this.files);
      },
      error: this.clientNotificationHandlerService.errorNotification,
    });
    this.uploadCompleteSubscription = this.fileUploadService.uploadedFileSubject.subscribe({
      next: () => {
        this.uploadInProgress = false;
        if (this.snackBarRef) {
          this.snackBarRef.dismiss();
        }
      },
      error: this.clientNotificationHandlerService.errorNotification,
    });
  }

  deleteAsset(assetId: string, planDeletionFlag?: boolean) {
    const dialogRef: MatDialogRef<
      AssetDeletionDialogComponent,
      { confirm: boolean; actionType: string; plannedDeletionObj?: UpdateAssetDto; deletionDate?: string }
    > = this.dialog.open(AssetDeletionDialogComponent, { data: { planDeletionFlag, planDeletion: this.assetInfo.plannedDeletion } });
    dialogRef.disableClose = true;
    dialogRef
      .afterClosed()
      .pipe(
        tap((data) => {
          if (data?.confirm) {
            switch (data.actionType) {
              case 'planDeletion': {
                this.assetCommonService.plannedForDeletion(assetId, data.plannedDeletionObj || {}, data.deletionDate || '');
                break;
              }
              case 'deleteAsset': {
                this.assetCommonService.assetsRemoveMultiple(assetId);
                break;
              }
            }
          }
        }),
      )
      .subscribe();
  }

  assetActionAccessibility(asset: AssetUIModel) {
    return (
      this.canPipe.transform(this.AccessAsset(asset), 'delete') ||
      (this.canPipe.transform(this.AccessAsset(asset), 'download') &&
        ![this.CustomClients.LufthansaMediacenter, this.CustomClients.LufthansaMedialounge].includes(this.brandingKey)) ||
      ([this.CustomClients.LufthansaMediacenter, this.CustomClients.LufthansaMedialounge].includes(this.brandingKey) &&
        this.screenSizeDetectorService.isSmallScreen) ||
      this.canPipe.transform(this.AccessAsset(asset), 'share') ||
      this.canPipe.transform(this.AccessAsset(asset), 'collection.access') ||
      this.canPipe.transform(this.AccessAsset(asset), 'update') ||
      this.canPipe.transform(this.AccessAsset(asset), 'usagerights') ||
      this.canPipe.transform(this.AccessAsset(asset), 'relations')
    );
  }

  assetActionEditAccessibility(asset: AssetUIModel) {
    return (
      this.canPipe.transform(this.AccessAsset(asset), 'update') ||
      this.canPipe.transform(this.AccessAsset(asset), 'usagerights') ||
      this.canPipe.transform(this.AccessAsset(asset), 'relations')
    );
  }

  downloadAsset(asset: AssetUIModel) {
    const downloadData = this.downloadAssetsService.createDownloadData([asset]);
    this.dialog.open<AssetDownloadComponent, AssetDownloadData>(AssetDownloadComponent, {
      width: '100%',
      maxWidth: '70rem',
      height: '100%',
      maxHeight: '43rem',
      disableClose: true,
      data: { data: downloadData },
    });
  }

  changeView(event: ViewType) {
    this.assetRelationsService.changeViewType(event);
  }

  ngOnDestroy(): void {
    this.progressSubscription?.unsubscribe();
    this.uploadStartSubscription?.unsubscribe();
    this.uploadCompleteSubscription?.unsubscribe();
    this.assetIDSubjectSubscription?.unsubscribe();
    this.assetCommonService.assetDataSubject$.next(null);
  }
}
